import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { StandingsTable } from "./Standings";
import { FixturesView, SchedulePreview } from "./Fixtures";
import LeagueChat from "../components/LeagueChat";
import LeagueFeed from "../components/LeagueFeed";
import TodaysGames from "../components/TodaysGames";
import WhatsAppButton from "../components/WhatsAppButton";
import { api as apiClient, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UserMinus } from "lucide-react";
import ReportPlayerButton from "../components/ReportPlayerButton";
import ModReportToAdminButton from "../components/ModReportToAdminButton";

export default function LeagueDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [league, setLeague] = useState(null);
  const [tab, setTab] = useState("standings");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${id}`);
        setLeague(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "League not available");
      }
    })();
  }, [id]);

  if (error) return <Panel className="font-mono text-[#E11D48] text-sm" testId="league-detail-error">[ {error} ]</Panel>;
  if (!league) return <TactLoader />;
  const pct = Math.min(100, Math.round((league.player_count / league.capacity) * 100));
  const isMember = user && user !== false && (
    user.role === "admin" || (league.players || []).includes(user.id)
  );
  const isAdmin = user?.role === "admin";
  const isMod = user?.role === "moderator";
  const tabs = ["standings", "fixtures", "players"];
  if (isMember && league.status === "locked") tabs.splice(0, 0, "today");
  if (isMember) tabs.push("feed", "chat");

  const removePlayer = async (playerId, username) => {
    if (!window.confirm(`Remove ${username} from ${league.name}? This will delete their fixtures in this league and free their slot.`)) return;
    try {
      await apiClient.delete(`/admin/leagues/${league.id}/players/${playerId}`);
      toast.success(`${username} removed from ${league.name}`);
      const { data } = await apiClient.get(`/leagues/${league.id}`);
      setLeague(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="league-detail-page" className="space-y-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// season</div>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-wider">{league.name}</h1>
          <div className="mt-2 font-mono text-xs text-[#71717A] uppercase tracking-widest">{league.status} · {league.player_count}/{league.capacity} PLAYERS</div>
        </div>
        <Link to="/leagues" className="border border-[#E4E4E7] px-4 py-2 text-xs uppercase tracking-widest hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors">← All Seasons</Link>
      </div>

      <div className="h-1.5 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7]">
        <div className="h-full bg-[#7C3AED]" style={{ width: `${pct}%` }} />
      </div>

      <div className="flex gap-2 border-b border-[#E4E4E7] dark:border-[#2A2A30] flex-wrap" data-testid="tabs">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-4 py-3 font-mono text-xs uppercase tracking-[0.3em] border-b-2 transition-colors ${tab === t ? "border-[#7C3AED] text-[#7C3AED]" : "border-transparent text-[#A1A1AA] hover:text-[#7C3AED]"}`}
          >{t}</button>
        ))}
      </div>

      {tab === "today" && isMember && <TodaysGames leagueId={league.id} />}
      {tab === "standings" && <StandingsTable leagueId={league.id} />}
      {tab === "fixtures" && (
        league.status === "open"
          ? <SchedulePreview activeLeague={league} />
          : <FixturesView leagueId={league.id} />
      )}
      {tab === "players" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="players-list">
          {league.players_detail?.length === 0 && (
            <Panel className="font-mono text-[#71717A] text-sm col-span-full">[ NO PLAYERS DRAFTED YET ]</Panel>
          )}
          {league.players_detail?.map((p, i) => (
            <div key={p.id} className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-4 flex flex-col gap-3" data-testid={`player-${p.id}`}>
              <div className="min-w-0">
                <div className={`font-heading text-lg uppercase break-words leading-tight ${user && user !== false && p.id === user.id ? "text-[#34C759]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>{p.username}</div>
                <div className="font-mono text-[10px] text-[#71717A] truncate">{p.game_id}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <WhatsAppButton number={p.whatsapp} testId={`whatsapp-${p.id}`} size="sm" />
                {user && user !== false && p.id !== user.id && (
                  <Link to={`/chat/${p.id}`} data-testid={`dm-${p.id}`} className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#7C3AED]/40 text-[#7C3AED] px-2 py-1 hover:bg-[#7C3AED] hover:text-white transition-colors">DM</Link>
                )}
                {isMember && user && user !== false && p.id !== user.id && (
                  <ReportPlayerButton leagueId={league.id} playerId={p.id} playerName={p.username} myId={user.id} />
                )}
                {isMod && user && user !== false && p.id !== user.id && (
                  <ModReportToAdminButton reportedUserId={p.id} reportedUsername={p.username} />
                )}
                {isAdmin && (
                  <button
                    type="button"
                    data-testid={`remove-player-${p.id}`}
                    onClick={() => removePlayer(p.id, p.username)}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#E11D48]/60 text-[#E11D48] px-2 py-1 hover:bg-[#E11D48] hover:text-white transition-colors inline-flex items-center gap-1"
                    title="Remove player from this league"
                  >
                    <UserMinus className="w-3 h-3" /> Remove
                  </button>
                )}
                <div className="font-mono text-[10px] text-[#7C3AED] tracking-[0.2em]">#{String(i + 1).padStart(2, "0")}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "feed" && isMember && <LeagueFeed leagueId={league.id} />}
      {tab === "chat" && isMember && <LeagueChat leagueId={league.id} />}
    </div>
  );
}
